/**
 * THE `?tab=` VOCABULARY OF EVERY SURFACE THAT HAS TABS — the ids a surface
 * answers to, and the ids it USED to answer to.
 *
 * WHY THIS EXISTS BEFORE THE PAGES DO. A tab id is an address before it is a
 * label: it is in somebody's bookmarks, on a printed procedure beside the till,
 * in a support note, in the audit trail. The POC has been in use long enough
 * to have retired several, and every one of them is still honoured there. If
 * this repo's surfaces were built first and their vocabularies second, the
 * first release would 404 — or worse, silently land on the default tab — every
 * address the POC's users already hold. Resolving them in `validateSearch`
 * means `/settings?tab=branches` already means the right place today, while
 * the page is still a placeholder.
 *
 * A RETIRED ID IS AN ALIAS, NEVER A DELETION. That is the POC's rule for every
 * surface, and this file is where it is kept: an alias maps onto the tab that
 * absorbed it, and is rewritten in the URL so the address somebody copies next
 * is the current one.
 *
 * AN UNKNOWN ID IS PASSED THROUGH, NOT REFUSED, and that is deliberate.
 * Settings and Reports take tabs that MODULES contribute through a registry
 * (POC `settings/contributions`, `reports/tabs`), so this core list cannot
 * tell a module's tab from a typo — and refusing it here would break a
 * module's bookmark before the module is built. The surface decides what an
 * id it has no tab for draws, when the surface exists. What IS refused is an
 * id that could not be one: anything outside `[a-z0-9-]`.
 *
 * IN `app/`, BESIDE `nav.ts`, and not in `features/<surface>/`. There is no
 * feature package for any of these surfaces yet, and the search palette will
 * read this list from the shell — which lint forbids reaching into a feature.
 * When a surface's package is born, its vocabulary moves into it and this file
 * re-exports it, so no route's import changes. `DECISIONS.md` 21-09-2026.
 *
 * Ids and aliases read off POC `main` @ dfdcc6e: `core/settings/sections.ts`,
 * `core/reports/tabs.ts`, `core/inventory/inventory-hub.tsx`.
 *
 * ONLY WHAT THE SIMPLE EDITION DRAWS (`CLAUDE.md` ▸ Scope). The MVP is Simple
 * retail, so Booking has no vocabulary here, Reports has no Overview and
 * Inventory has no Counts. Nothing out of scope is built, and an id for it
 * would be an address that promises a screen.
 */
import { z } from "zod";

export interface TabVocabulary {
  /** The core tabs, in the order the strip draws them. */
  tabs: readonly string[];
  /** Retired id → the tab that absorbed it. */
  aliases: Readonly<Record<string, string>>;
}

export const TABS = {
  settings: {
    tabs: ["pharmacy", "staff", "access", "log"],
    aliases: {
      // The tab that listed the branches was folded into Pharmacy & branches.
      branches: "pharmacy",
      // Asked two questions (language, density), both answered elsewhere now.
      preferences: "pharmacy",
      // Existed for one release, while the layout was the terminal's. It is the
      // reader's now and lives in the top bar — but an address that has been in
      // a URL resolves, even a short-lived one.
      terminal: "pharmacy",
    },
  },
  reports: {
    tabs: ["money", "inventory", "books", "audit"],
    aliases: {},
  },
  inventory: {
    tabs: [
      "stock",
      "products",
      "pricing",
      "suppliers",
      "supply-items",
      "procurement",
      "receipts",
      "adjustments",
      "movements",
    ],
    aliases: {
      // Returns and corrections became one Adjustments tab.
      waste: "adjustments",
      // The product list stopped being only medicines; the id moved with the word.
      formulary: "products",
      // The shelf's day-end is gone — every supply moves the shelf when it is
      // made — and what survives of it (a line that could not be shelved) is
      // drawn on Stock. It was opened every evening, so it is in bookmarks.
      close: "stock",
    },
  },
} as const satisfies Record<string, TabVocabulary>;

export type TabbedSurface = keyof typeof TABS;

const TAB_ID = /^[a-z0-9][a-z0-9-]*$/;

/**
 * The tab an address means. Absent stays absent — the surface draws its own
 * first tab, and an address without `?tab=` is not rewritten to carry one.
 */
export function resolveTab(surface: TabbedSurface, requested: unknown): string | undefined {
  if (typeof requested !== "string") return undefined;
  const id = requested.trim().toLowerCase();
  if (!TAB_ID.test(id)) return undefined;
  const aliases: Readonly<Record<string, string>> = TABS[surface].aliases;
  return aliases[id] ?? id;
}

/** `validateSearch` for a surface whose only search state is its tab. */
export function tabSearch(surface: TabbedSurface) {
  return z.object({
    tab: z
      .unknown()
      .optional()
      .transform((value) => resolveTab(surface, value)),
  });
}
